import { calculateMuscleGroupCountsByDate } from "./muscleGroupService";
import { getExerciseLog, saveExerciseLog } from "../utils/localStorageUtils";
import { muscleGroups } from "../data/muscleGroups";
import { todayYMDLocal } from "../utils/dateUtils";

/**
 * Retrieves workout data for the last active workout days (max 7 + today).
 * Returns selected dates, muscle group counts, and today's exercises.
 */
export const getMuscleGroupCountsAndExercises = () => {
    const log = getExerciseLog();

    // Get all dates with recorded exercises
    const activeDates = Object.keys(log)
        .filter(date => Object.keys(log[date]).length > 0) // Only include days with workouts
        .sort((a, b) => new Date(b) - new Date(a)); // Sort from newest to oldest

    // Get today's date (local)
    const today = todayYMDLocal();

    // Always include today, even if it's empty
    if (!activeDates.includes(today)) {
        activeDates.unshift(today);
    }
    
    // Select last 7 active days + today
    let lastDates = activeDates.slice(0, 8); // Max 7 + today
    
    // Ensure all dates with exercises are included in the displayed dates
    // This is important when a user logs an exercise for a date that's not today
    // and not one of the last 7 active days
    Object.keys(log).forEach(date => {
        if (Object.keys(log[date]).length > 0 && !lastDates.includes(date)) {
            lastDates.push(date);
        }
    });
    
    // Ensure total number of shown days is at least 3.
    // Always include today; add yesterday/day before only if needed to reach 3.
    const pad2 = (n) => String(n).padStart(2, "0");
    const base = new Date();
    let back = 0;
    while (lastDates.length < 3) {
        const d = new Date(base);
        d.setDate(base.getDate() - back);
        const ds = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
        if (!lastDates.includes(ds)) {
            lastDates.push(ds);
        }
        back += 1;
    }

    // Sort dates from newest to oldest
    lastDates.sort((a, b) => new Date(b) - new Date(a));

    // Initialize exercise counts for each muscle group
    const initialCounts = {};
    muscleGroups.forEach((group) => {
        initialCounts[group.id] = lastDates.reduce((acc, date) => {
            acc[date] = { primary: 0, secondary: 0 }; // Default: 0 sets
            return acc;
        }, {});
    });

    // Compute exercise counts for each muscle group by date
    const counts = calculateMuscleGroupCountsByDate(lastDates, log, initialCounts);

    // Get today's exercises
    const todayLog = log[today] || {};
    const todayExercises = Object.keys(todayLog).map((exerciseId) => ({
        exerciseId,
        sets: todayLog[exerciseId],
    }));

    return { lastDates, counts, todayExercises };
};

/**
 * Removes an exercise from today's log by ID.
 * @param {Array} dates - List of dates (not used, kept for backward compatibility).
 * @param {string} exerciseId - ID of the exercise to delete.
 */
export const removeExerciseFromTodayLog = (dates, exerciseId) => {
    const log = getExerciseLog();
    const today = todayYMDLocal();
    const todayLog = log[today] || {};

    // Remove the exercise from today's log
    delete todayLog[exerciseId];

    log[today] = todayLog;
    saveExerciseLog(log);
};
